import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { WidePageLayout } from '../components/WidePageLayout';
import RichTextContent from '../components/RichTextContent';
import useEpicDetail from '../hooks/useEpicDetail';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import Edit2Icon from '../components/icons/Edit2Icon';
import Trash2Icon from '../components/icons/Trash2Icon';
import ChevronRightIcon from '../components/icons/ChevronRightIcon';
import ChevronDownIcon from '../components/icons/ChevronDownIcon';

export function EpicDetail() {
  const { id: boardId, epicId } = useParams<{ id: string; epicId: string }>();
  const navigate = useNavigate();
  const { epic, deleteEpic } = useEpicDetail(epicId || '0');
  useDocumentTitle(epic?.name);
  const [isContentExpanded, setIsContentExpanded] = useState(false);

  const handleDelete = async () => {
    if (!epic) return;

    if (confirm(`Are you sure you want to delete epic "${epic.name}"?`)) {
      try {
        await deleteEpic();
        navigate(`/boards/${boardId}/epics`);
      } catch {
        // Error is already handled by the hook with toast
      }
    }
  };

  if (!epic) {
    return (
      <div className="space-y-6">
        <WidePageLayout>
          <div className="text-center py-12">
            <div className="text-gray-400">Loading epic...</div>
          </div>
        </WidePageLayout>
      </div>
    );
  }

  const defaultColor = '#3b82f6';
  const color = epic.color || defaultColor;
  const hasContent = epic.content && Array.isArray(epic.content) && epic.content.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <WidePageLayout>
        <div>
          <Link
            to={`/boards/${boardId}/epics`}
            className="text-gray-400 hover:text-white transition-colors text-sm"
          >
            ← Back to Epics
          </Link>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-3">
              <div className="w-1 h-12 rounded" style={{ backgroundColor: color }} />
              <div>
                <h1 className="text-3xl font-bold text-white">{epic.name}</h1>
                {epic.closed && (
                  <span className="inline-block px-2 py-1 bg-gray-700 text-gray-400 text-xs rounded mt-1">
                    Closed
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Link
                to={`/boards/${boardId}/epics/${epic.id}/edit`}
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Edit2Icon className="w-4 h-4" />
                Edit
              </Link>
              <button
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Trash2Icon className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </WidePageLayout>

      {/* Content */}
      <WidePageLayout>
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          {/* Color header bar */}
          <div className="h-2" style={{ backgroundColor: color }} />

          <div className="p-6">
            {/* Description */}
            {epic.description && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-white mb-2">Description</h2>
                <p className="text-gray-300">{epic.description}</p>
              </div>
            )}

            {/* Rich Text Content - Collapsible */}
            {hasContent && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold text-white">Details</h2>
                  <button
                    onClick={() => setIsContentExpanded(!isContentExpanded)}
                    className="text-blue-500 hover:text-blue-400 text-sm font-medium flex items-center gap-1 transition-colors"
                  >
                    {isContentExpanded ? (
                      <>
                        <span className="w-4 h-4">
                          <ChevronDownIcon />
                        </span>
                        Collapse
                      </>
                    ) : (
                      <>
                        <span className="w-4 h-4">
                          <ChevronRightIcon />
                        </span>
                        Expand
                      </>
                    )}
                  </button>
                </div>

                <div className="relative">
                  {isContentExpanded ? (
                    <RichTextContent content={epic.content} />
                  ) : (
                    <>
                      <div className="max-h-[200px] overflow-hidden relative">
                        <RichTextContent content={epic.content} />
                      </div>
                      <div className="h-24 bg-gradient-to-t from-gray-800 to-transparent absolute bottom-0 left-0 right-0 pointer-events-none -mt-20" />
                      <div className="mt-4 pt-4">
                        <button
                          onClick={() => setIsContentExpanded(true)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          Read More
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Metrics */}
            <div className="mb-6 pt-6 border-t border-gray-700">
              <h2 className="text-lg font-semibold text-white mb-3">Metrics</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-1">
                    <span>📊</span>
                    <span className="text-sm">Cards</span>
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {epic.metrics?.card_count || 0}
                  </div>
                </div>
                <div className="bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-1">
                    <span>✅</span>
                    <span className="text-sm">Completed</span>
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {epic.metrics?.completed_cards_count || 0}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {epic.metrics?.card_count && epic.metrics?.completed_cards_count !== undefined
                      ? `${Math.round((epic.metrics.completed_cards_count / epic.metrics.card_count) * 100)}% complete`
                      : '0% complete'}
                  </div>
                </div>
                {epic.depth_limit !== null && epic.depth_limit !== undefined && (
                  <div className="bg-gray-900 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-gray-400 mb-1">
                      <span>🔄</span>
                      <span className="text-sm">Max Depth</span>
                    </div>
                    <div className="text-2xl font-bold text-white">{epic.depth_limit}</div>
                  </div>
                )}
                {epic.parent_epic_id && (
                  <div className="bg-gray-900 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-gray-400 mb-1">
                      <span>🔗</span>
                      <span className="text-sm">Parent</span>
                    </div>
                    <div className="text-lg font-bold text-white">Yes</div>
                  </div>
                )}
              </div>
            </div>

            {/* Metadata */}
            <div className="pt-6 border-t border-gray-700">
              <h2 className="text-lg font-semibold text-white mb-3">Metadata</h2>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 w-24">Created:</span>
                  <span className="text-gray-300">
                    {epic.created_at ? new Date(epic.created_at).toLocaleString() : 'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 w-24">Updated:</span>
                  <span className="text-gray-300">
                    {epic.updated_at ? new Date(epic.updated_at).toLocaleString() : 'N/A'}
                  </span>
                </div>
                {epic.date_last_activity && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 w-24">Last Activity:</span>
                    <span className="text-gray-300">
                      {new Date(epic.date_last_activity).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Links to Cards */}
            <div className="pt-6 border-t border-gray-700 mt-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-white">Linked Cards</h2>
                <Link to={`/boards/${boardId}`} className="text-blue-500 hover:underline text-sm">
                  View in Board →
                </Link>
              </div>
              {epic.cards && epic.cards.length > 0 ? (
                <div className="space-y-2">
                  {epic.cards.map((card) => (
                    <Link
                      key={card.id}
                      to={`/boards/${boardId}/cards/${card.id}`}
                      className="block bg-gray-900 hover:bg-gray-700 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-gray-500 text-sm">#{card.id_short}</span>
                            <h3 className="text-white font-medium">{card.name}</h3>
                          </div>
                          {card.description && (
                            <p className="text-gray-400 text-sm line-clamp-1">{card.description}</p>
                          )}
                        </div>
                        {card.due && (
                          <span
                            className={`text-sm px-2 py-1 rounded ${
                              card.due_complete
                                ? 'bg-green-900 text-green-300'
                                : 'bg-yellow-900 text-yellow-300'
                            }`}
                          >
                            Due: {new Date(card.due).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <div className="text-4xl mb-2">📭</div>
                  <p>No cards linked to this epic yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </WidePageLayout>
    </div>
  );
}

export default EpicDetail;
